import type { BookType } from "@book-club/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { addMonths } from "date-fns";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toScheduledAtIsoFromPickerDay } from "@/features/books/utils/sessionScheduledAt";
import logger from "@/logger";
import { type ScheduleBookFormData, scheduleBookFormSchema } from "../schemas/scheduleBookForm.schema";

type FormReturn = UseFormReturn<ScheduleBookFormData>;

type UseScheduleBookFormReturn = {
    // Watched values
    author: string | undefined;
    bookType: BookType | undefined;
    month: number;
    selectedBookId: string | null;
    sessionDates: Date[];
    themeTitle: string | undefined;
    year: number;

    // Form methods
    formMethods: FormReturn;
    handleSubmit: FormReturn["handleSubmit"];

    // Custom handlers
    handleBookSelect: (bookId: string) => void;
    handleMonthYearChange: (year: number, month: number) => void;
    handleSessionDatesChange: (dates: Date[]) => void;
    handleTypeChange: (type: BookType) => void;

    // Computed values
    isFormValid: boolean;
    prepareScheduleData: () => {
        author?: string;
        bookId: string | null;
        bookType: BookType;
        month: number;
        sessionDates: string[];
        themeTitle?: string;
        year: number;
    } | null;
};

/**
 * Custom hook to manage schedule book form state using react-hook-form
 * Handles book type selection, book selection, month/year selection, and author input
 */
export function useScheduleBookForm(): UseScheduleBookFormReturn {
    // Calculate one month in advance, handling year rollover correctly
    const nextMonth = addMonths(new Date(), 1);
    const initialYear = nextMonth.getFullYear();
    const initialMonth = nextMonth.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

    const formMethods = useForm<ScheduleBookFormData>({
        defaultValues: {
            author: undefined,
            bookType: undefined,
            month: initialMonth,
            selectedBookId: null,
            sessionDates: [],
            themeTitle: undefined,
            year: initialYear,
        },
        mode: "onChange",
        resolver: zodResolver(scheduleBookFormSchema),
    });

    const { handleSubmit: rhfHandleSubmit, setValue, watch } = formMethods;

    // Watch form values for reactive updates
    const author = watch("author");
    const bookType = watch("bookType");
    const month = watch("month");
    const selectedBookId = watch("selectedBookId");
    const sessionDates = watch("sessionDates");
    const themeTitle = watch("themeTitle");
    const year = watch("year");

    logger.debug("useScheduleBookForm", { author, bookType, month, selectedBookId, sessionDates, themeTitle, year });

    const handleTypeChange = (type: BookType) => {
        setValue("bookType", type, { shouldValidate: true });
        // Reset form when type changes
        setValue("selectedBookId", null, { shouldValidate: true });
        setValue("author", undefined, { shouldValidate: true });
        setValue("themeTitle", "", { shouldValidate: true });
        setValue("sessionDates", [], { shouldValidate: true });
    };

    const handleBookSelect = (bookId: string) => {
        setValue("selectedBookId", bookId, { shouldValidate: true });
    };

    const handleMonthYearChange = (newYear: number, newMonth: number) => {
        setValue("year", newYear, { shouldValidate: true });
        setValue("month", newMonth, { shouldValidate: true });
        // Clear session dates when month/year changes since they're no longer valid
        setValue("sessionDates", [], { shouldValidate: true });
    };

    const handleSessionDatesChange = (dates: Date[]) => {
        setValue("sessionDates", dates, { shouldValidate: true });
    };

    const hasRequiredBaseFields = Boolean(bookType && year && month && sessionDates.length > 0);

    const isBookTypeValid = (): boolean => {
        if (bookType === "specificBook") {
            return selectedBookId !== null && selectedBookId.length > 0;
        }
        if (bookType === "authorFocus") {
            return author !== undefined && author.trim().length > 0;
        }
        return true; // freeChoice only needs base fields
    };

    const computeIsFormValid = (): boolean => {
        return hasRequiredBaseFields && isBookTypeValid();
    };

    const isFormValidComputed = computeIsFormValid();

    // Prepare data for submission
    const prepareScheduleData = (): {
        author?: string;
        bookId: string | null;
        bookType: BookType;
        month: number;
        sessionDates: string[];
        themeTitle?: string;
        year: number;
    } | null => {
        if (!isFormValidComputed || !bookType) {
            return null;
        }

        const sessionDatesISO = sessionDates.map((date) => toScheduledAtIsoFromPickerDay(new Date(date)));

        return {
            author: bookType === "authorFocus" ? author : undefined,
            bookId: bookType === "specificBook" ? selectedBookId : null,
            bookType,
            month,
            sessionDates: sessionDatesISO,
            ...(bookType === "freeChoice" && themeTitle?.trim() && { themeTitle: themeTitle.trim() }),
            year,
        };
    };

    return {
        author,
        bookType: bookType ?? undefined,
        formMethods,
        handleBookSelect,
        handleMonthYearChange,
        handleSessionDatesChange,
        handleSubmit: rhfHandleSubmit,
        handleTypeChange,
        isFormValid: isFormValidComputed,
        month,
        prepareScheduleData,
        selectedBookId,
        sessionDates,
        themeTitle,
        year,
    };
}
